import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, XCircle, AlertTriangle, Send, Eraser, Flag } from 'lucide-react';
import { useInspectionStore } from '@/context/inspectionStore';
import { submitInspection } from '@/services/api';
import toast from 'react-hot-toast';
import { queueInspection } from '@/services/offlineDB';
import { useOffline } from '@/context/offlineContext';
import { createFreshChecklist } from '@/utils/checklistTemplate';
import type { Inspection, InspectionType } from '@/types';

export function ReviewPage() {
  const navigate = useNavigate();
  const inspection = useInspectionStore((s) => s.activeInspection);
  const setOverallNotes = useInspectionStore((s) => s.setOverallNotes);
  const setSignature = useInspectionStore((s) => s.setSignature);
  const setMileage = useInspectionStore((s) => s.setMileage);
  const setInspectorFlagged = useInspectionStore((s) => s.setInspectorFlagged);
  const setFlagReason = useInspectionStore((s) => s.setFlagReason);
  const isReady = useInspectionStore((s) => s.isReadyToSubmit());
  const stats = useInspectionStore((s) => s.getCompletionStats());
  const cancelInspection = useInspectionStore((s) => s.cancelInspection);
  const { isOnline, refreshQueueCount } = useOffline();
  const [submitting, setSubmitting] = React.useState(false);
  const vehiclePhotos = useInspectionStore(s => s.vehiclePhotos);

  React.useEffect(() => {
    if (!inspection) navigate('/');
  }, [inspection, navigate]);

  if (!inspection) return null;

  const failedChecks = inspection.categories.flatMap((cat) =>
    cat.checks.filter((ch) => ch.status === 'fail').map((ch) => ({ category: cat.name, ...ch }))
  );
  const incompleteChecks = inspection.categories.flatMap((cat) =>
    cat.checks.filter((ch) => ch.required && ch.status === null).map((ch) => ({ category: cat.name, ...ch }))
  );

  async function handleSubmit() {
    if (!inspection || submitting) return;
    const vehiclePhotos = useInspectionStore.getState().vehiclePhotos;
    setSubmitting(true);
    try {
      const final = {
        ...inspection,
        status: inspection.inspectorFlagged ? ('flagged' as const) : ('submitted' as const),
        submittedAt: new Date().toISOString(),
        ...(inspection.inspectorFlagged ? { flaggedAt: new Date().toISOString() } : {}),
        vehiclePhotos,
      };

      if (!isOnline) {
        await queueInspection(final);
        await refreshQueueCount();
        toast.success('Saved offline — will submit when reconnected');
        cancelInspection();
        navigate('/');
        return;
      }

      await submitInspection(final);
      toast.success('Inspection submitted successfully!');
      cancelInspection();
      navigate('/');
    } catch (err) {
      console.error('Submit error:', err);
      try {
        await queueInspection({
          ...inspection,
          status: inspection.inspectorFlagged ? ('flagged' as const) : ('submitted' as const),
          submittedAt: new Date().toISOString(),
          ...(inspection.inspectorFlagged ? { flaggedAt: new Date().toISOString() } : {}),
        });
        await refreshQueueCount();
        toast.success('Saved offline — will submit when reconnected');
        cancelInspection();
        navigate('/');
      } catch {
        toast.error('Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  // ── Dev-only: submit a fully populated dummy inspection ──
  async function handleDevSubmit(type: InspectionType) {
    if (submitting) return;
    setSubmitting(true);
    try {
      const categories = createFreshChecklist(type).map(cat => ({
        ...cat,
        checks: cat.checks.map(ch => ({
          ...ch,
          // Mark ~80% pass, ~20% fail for realism
          status: (Math.random() > 0.2 ? 'pass' : 'fail') as 'pass' | 'fail',
          notes: Math.random() > 0.7 ? 'Dev test note' : '',
        })),
      }));

      const dummy: Inspection = {
        id: `dev-${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
        vehicleId: inspection?.vehicleId ?? 'dev-vehicle-001',
        vehicleUnitNumber: inspection?.vehicleUnitNumber ?? 'DEV-001',
        inspectorId: 'dev-user-001',
        inspectorName: 'Dev User',
        type,
        status: 'submitted',
        startedAt: new Date(Date.now() - 15 * 60 * 1000).toISOString(), // 15 min ago
        submittedAt: new Date().toISOString(),
        mileage: 12345,
        categories,
        overallNotes: `[DEV] Dummy ${type} inspection submitted for testing.`,
        signature: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==',
        vehiclePhotos: [],
      };

      await submitInspection(dummy);
      toast.success(`[DEV] Dummy ${type} inspection submitted`);
      cancelInspection();
      navigate('/');
    } catch (err: any) {
      console.error('[DEV] Dummy submit error:', err);
      toast.error(`[DEV] Submit failed: ${err?.message ?? 'Unknown error'}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="mx-auto max-w-lg px-4 py-6 pb-32 dark:bg-gray-800 min-h-full">
      <button onClick={() => navigate('/inspect')} className="mb-4 flex items-center gap-1 text-sm font-medium text-wire3-navy dark:text-wire3-green">
        <ArrowLeft className="h-4 w-4" />
        Back to checklist
      </button>

      <h1 className="text-2xl font-bold text-wire3-navy dark:text-wire3-green">Review & Submit</h1>
      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
        {inspection.type.replace('-', ' ')} — {inspection.vehicleUnitNumber}
      </p>

      {/* Summary stats */}
      <div className="mt-5 grid grid-cols-3 gap-3">
        <div className="card dark:bg-gray-750 dark:border-gray-700 text-center">
          <CheckCircle2 className="mx-auto h-6 w-6 text-status-pass" />
          <p className="mt-1 text-2xl font-bold text-status-pass">{stats.passed}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Passed</p>
        </div>
        <div className="card dark:bg-gray-750 dark:border-gray-700 text-center">
          <XCircle className="mx-auto h-6 w-6 text-status-fail" />
          <p className="mt-1 text-2xl font-bold text-status-fail">{stats.failed}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Failed</p>
        </div>
        <div className="card dark:bg-gray-750 dark:border-gray-700 text-center">
          <AlertTriangle className="mx-auto h-6 w-6 text-status-warning" />
          <p className="mt-1 text-2xl font-bold text-status-warning">{incompleteChecks.length}</p>
          <p className="text-xs text-gray-500 dark:text-gray-400">Incomplete</p>
        </div>
      </div>

      {vehiclePhotos.some(vp => vp.photo?.pendingUpload) && (
        <div className="mt-4 rounded-lg border border-status-warning/30 bg-status-warning/5 p-4">
          <h3 className="text-sm font-semibold text-status-warning">⚠ Photos Pending Upload</h3>
          <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">
            {vehiclePhotos.filter(vp => vp.photo?.pendingUpload).length} photo{vehiclePhotos.filter(vp => vp.photo?.pendingUpload).length > 1 ? 's' : ''} will be uploaded and analyzed when you reconnect.
            The inspection will be queued and auto-submitted once analysis is complete.
          </p>
        </div>
      )}

      {/* Failed items */}
      {failedChecks.length > 0 && (
        <div className="mt-5">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-status-fail">Failed Items</h3>
          <div className="mt-2 flex flex-col gap-2">
            {failedChecks.map((ch) => (
              <div key={ch.id} className="rounded-lg border border-status-fail/20 bg-status-fail/5 dark:bg-status-fail/10 p-3">
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{ch.label}</p>
                <p className="text-xs text-gray-500 dark:text-gray-400">{ch.category}</p>
                {ch.notes && <p className="mt-1 text-xs text-gray-600 dark:text-gray-400 italic">"{ch.notes}"</p>}
                {ch.photos.length > 0 && (
                  <div className="mt-2 flex gap-1">
                    {ch.photos.map((p) => (
                      <img key={p.id} src={p.uri} className="h-12 w-12 rounded object-cover" alt="" />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Incomplete items warning */}
      {incompleteChecks.length > 0 && (
        <div className="mt-5 rounded-lg border border-status-warning/30 bg-status-warning/5 dark:bg-status-warning/10 p-4">
          <h3 className="text-sm font-semibold text-status-warning">Incomplete Required Checks</h3>
          <ul className="mt-2 space-y-1">
            {incompleteChecks.map((ch) => (
              <li key={ch.id} className="text-sm text-gray-600 dark:text-gray-400">
                • {ch.label} <span className="text-xs text-gray-400">({ch.category})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Mileage */}
      <div className="mt-5">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Current Odometer (miles)</label>
        <input
          type="number"
          value={inspection.mileage || ''}
          onChange={(e) => setMileage(parseInt(e.target.value) || 0)}
          placeholder="Enter current mileage"
          className="input-field mt-1"
        />
      </div>

      {/* Overall notes */}
      <div className="mt-5">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Overall Notes</label>
        <textarea
          value={inspection.overallNotes}
          onChange={(e) => setOverallNotes(e.target.value)}
          placeholder="Any additional notes about this inspection..."
          rows={3}
          className="input-field mt-1"
        />
      </div>

      {/* Flag for review */}
      <div className="mt-5 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300">
          <input
            type="checkbox"
            checked={!!inspection.inspectorFlagged}
            onChange={(e) => setInspectorFlagged(e.target.checked)}
            className="h-4 w-4 rounded border-gray-300 text-status-fail focus:ring-status-fail"
          />
          <Flag className="h-4 w-4 text-status-fail" />
          Flag this inspection for review
        </label>
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Use this if something about this vehicle or inspection needs a supervisor's attention.
        </p>
        {inspection.inspectorFlagged && (
          <textarea
            value={inspection.flagReason ?? ''}
            onChange={(e) => setFlagReason(e.target.value)}
            placeholder="Why are you flagging this inspection?"
            rows={2}
            className="input-field mt-3"
          />
        )}
      </div>

      {/* Signature pad */}
      <div className="mt-5">
        <label className="text-sm font-semibold text-gray-700 dark:text-gray-300">Inspector Signature</label>
        <SignaturePad value={inspection.signature} onChange={setSignature} />
      </div>

      {/* ── DEV ONLY: dummy submission buttons ── */}
      {import.meta.env.DEV && (
        <div className="mt-8 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-600 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-wider text-gray-400">[DEV] Submit Dummy Inspection</p>
          <p className="mb-3 text-xs text-gray-400 dark:text-gray-500">
            Instantly submits a fully populated fake inspection with randomised pass/fail results.
            No photos or signatures required.
          </p>
          <div className="flex flex-col gap-2">
            {(['daily', 'weekly', 'monthly'] as InspectionType[]).map(type => (
              <button
                key={type}
                onClick={() => handleDevSubmit(type)}
                disabled={submitting}
                className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 py-2 text-xs font-semibold capitalize text-gray-500 dark:text-gray-400 hover:border-wire3-green hover:text-wire3-green transition-colors disabled:opacity-50"
              >
                Submit dummy {type} inspection
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Submit button */}
      <div className="fixed bottom-16 left-0 right-0 z-20 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 px-4 py-3 sm:bottom-0">
        <div className="mx-auto max-w-lg">
          <button onClick={handleSubmit} disabled={!isReady || submitting} className="btn-primary w-full disabled:opacity-50">
            <Send className="h-4 w-4" />
            {submitting ? 'Submitting...' : 'Submit Inspection'}
          </button>
          {!isReady && (
            <p className="mt-2 text-center text-xs text-status-warning">
              {inspection.inspectorFlagged && !inspection.flagReason?.trim()
                ? "Please describe why you're flagging this inspection."
                : 'Complete all required checks, mileage, and signature to submit.'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

function SignaturePad({ value, onChange }: { value?: string; onChange: (dataUri: string) => void }) {
  const canvasRef = React.useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = React.useState(false);

  function getCtx() { return canvasRef.current?.getContext('2d') ?? null; }

  function getPos(e: React.TouchEvent | React.MouseEvent) {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX;
    const clientY = 'touches' in e ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * (canvas.width / rect.width),
      y: (clientY - rect.top) * (canvas.height / rect.height),
    };
  }

  function startDraw(e: React.TouchEvent | React.MouseEvent) {
    const ctx = getCtx(); if (!ctx) return;
    setDrawing(true);
    const { x, y } = getPos(e);
    ctx.beginPath(); ctx.moveTo(x, y);
  }

  function draw(e: React.TouchEvent | React.MouseEvent) {
    if (!drawing) return;
    const ctx = getCtx(); if (!ctx) return;
    const { x, y } = getPos(e);
    ctx.lineWidth = 2.5; ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0d1b3e';
    ctx.lineTo(x, y); ctx.stroke();
  }

  function endDraw() {
    setDrawing(false);
    const canvas = canvasRef.current;
    if (canvas) onChange(canvas.toDataURL('image/png'));
  }

  function clear() {
    const ctx = getCtx(); const canvas = canvasRef.current;
    if (ctx && canvas) { ctx.clearRect(0, 0, canvas.width, canvas.height); onChange(''); }
  }

  return (
    <div className="mt-1">
      <div className="relative rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700">
        <canvas
          ref={canvasRef} width={600} height={200}
          className="signature-canvas h-28 w-full rounded-lg"
          onMouseDown={startDraw} onMouseMove={draw} onMouseUp={endDraw} onMouseLeave={endDraw}
          onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={endDraw}
        />
        {!value && (
          <p className="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-gray-300 dark:text-gray-500">
            Sign here
          </p>
        )}
      </div>
      <button onClick={clear} className="mt-1 flex items-center gap-1 text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
        <Eraser className="h-3 w-3" /> Clear signature
      </button>
    </div>
  );
}
